import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Mail, ArrowLeft, Accessibility } from 'lucide-react';

const Measure = ({ children }) => (
  <li className="flex items-start gap-2.5 text-[#374151] leading-relaxed">
    <CheckCircle2 className="w-5 h-5 text-[#3d7a22] shrink-0 mt-0.5" aria-hidden="true" />
    <span>{children}</span>
  </li>
);

export default function AccessibilityPage() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="accessibility-page">
      <a href="#main-content" className="skip-to-content">Skip to main content</a>

      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/lifelink-logo.png" alt="LIFE Link" className="h-10 w-auto object-contain" />
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#34657f] hover:underline rounded px-2 py-1.5"
            data-testid="accessibility-back-link"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to sign in
          </Link>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#34657f] text-white">
            <Accessibility className="w-6 h-6" aria-hidden="true" />
          </span>
          <h1 className="font-['Manrope'] text-3xl sm:text-4xl font-bold text-[#1A1A1A]">
            Accessibility Statement
          </h1>
        </div>
        <p className="text-sm text-[#5C5C5C] mb-8">Last reviewed: June 2026</p>

        <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="font-['Manrope'] text-lg font-bold text-[#1A1A1A] mb-3">Our commitment</h2>
          <p className="text-[#374151] leading-relaxed">
            LIFE Link — the Project LIFE portal from Butler Tech — is committed to ensuring digital
            accessibility for everyone, including people with disabilities. Accessibility is central to our
            mission of helping students with disabilities build work and life skills, and we continually
            improve the user experience for all visitors.
          </p>
        </section>

        <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="font-['Manrope'] text-lg font-bold text-[#1A1A1A] mb-3">Conformance status</h2>
          <p className="text-[#374151] leading-relaxed">
            LIFE Link is designed to conform to the{' '}
            <a
              href="https://www.w3.org/TR/WCAG22/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1f7fa6] underline font-medium"
            >
              Web Content Accessibility Guidelines (WCAG) 2.2, Level AA
            </a>
            . These guidelines explain how to make web content more accessible to people with a wide range
            of disabilities, including visual, auditory, motor, and cognitive disabilities.
          </p>
        </section>

        <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="font-['Manrope'] text-lg font-bold text-[#1A1A1A] mb-3">Measures we take</h2>
          <ul className="space-y-2.5">
            <Measure>Color contrast of at least 4.5:1 for all text and 3:1 for interactive components and graphics (WCAG 1.4.3, 1.4.11)</Measure>
            <Measure>Full keyboard navigation, including a "Skip to main content" link on every page (WCAG 2.1.1, 2.4.1)</Measure>
            <Measure>Visible focus indicators on all interactive elements (WCAG 2.4.7, 2.4.11)</Measure>
            <Measure>Descriptive labels and ARIA attributes on all buttons, icons, form fields, and menus for screen reader users (WCAG 1.1.1, 4.1.2)</Measure>
            <Measure>Touch and click targets of at least 24×24 pixels (WCAG 2.5.8)</Measure>
            <Measure>Logical heading structure and landmark regions on every page (WCAG 1.3.1, 2.4.6)</Measure>
            <Measure>Text can be resized up to 200% without loss of content or functionality (WCAG 1.4.4)</Measure>
            <Measure>Automated audits with axe-core plus manual keyboard and screen reader checks on every major release</Measure>
          </ul>
        </section>

        <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="font-['Manrope'] text-lg font-bold text-[#1A1A1A] mb-3">Known limitations</h2>
          <ul className="list-disc pl-5 space-y-2.5 text-[#374151] leading-relaxed">
            <li>Some third-party content (e.g., documents hosted in Google Drive and embedded previews) is created outside this portal and may not fully meet WCAG 2.2 AA. We work with content authors to improve source documents where possible.</li>
            <li>User-generated forum content (photos, GIFs) may occasionally lack alternative text. We encourage all members to add descriptions to images they post.</li>
          </ul>
        </section>

        <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="font-['Manrope'] text-lg font-bold text-[#1A1A1A] mb-3">Feedback and contact</h2>
          <p className="text-[#374151] leading-relaxed mb-4">
            We welcome your feedback on the accessibility of LIFE Link. If you encounter an accessibility
            barrier, or need content in an alternative format, please let us know — we aim to respond
            within 5 business days.
          </p>
          <a
            href="mailto:accessibility@aspiritech.org"
            className="inline-flex items-center gap-2 bg-[#34657f] hover:bg-[#2a5060] text-white font-semibold rounded-lg px-4 py-2.5 transition-colors"
            data-testid="accessibility-contact-link"
          >
            <Mail className="w-4 h-4" aria-hidden="true" />
            accessibility@aspiritech.org
          </a>
        </section>

        <p className="text-sm text-[#5C5C5C] text-center pb-6">
          &copy; 2026 LIFE Link | Project LIFE | Butler Tech. All rights reserved.
        </p>
      </main>
    </div>
  );
}
